import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import { useRouter } from 'next/router';
import { renderHTML } from '@agility/nextjs';
import { KitGridContainer, KitGridItem, KitButton } from 'components/common/cms-kit';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { getColorByAgilityName } from '../../services/agilityTranslations';

const SeriesName = styled('div')(({ theme }) => ({
  fontWeight: 300,
  textTransform: 'uppercase',
  [theme.breakpoints.up('md')]: {
    margin: '15% 0 0 0',
    fontSize: '1.55rem',
  },
  [theme.breakpoints.down('md')]: {
    margin: '20px 0 0 0',
    fontSize: '1.10rem',
  },
}));
const VideoTitle = styled('div')(({ theme }) => ({
  color: theme.palette.common.white,
  fontWeight: 600,
  [theme.breakpoints.up('md')]: {
    fontSize: '1.40rem',
    lineHeight: '1.50rem',
    margin: '10px 0',
    maxWidth: '80%',
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '1.65rem',
    lineHeight: '1.80rem',
    margin: '15px 0',
    maxWidth: '95%',
  },
}));
const Description = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    fontSize: '1.00rem',
    lineHeight: '1.35rem',
    height: '100%',
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '1.00rem',
    lineHeight: '1.35rem',
    height: '100%',
  },
}));
const _ArrowLeftCorner = styled('div')(({ theme: _theme }) => ({
  borderLeft: '100px solid #B34520',
  borderRight: '100px solid transparent',
  borderBottom: '100px solid transparent',
  height: 0,
  width: 0,
  position: 'absolute',
  left: 0,
  top: 0,
  zIndex: 2,
}));
const _ArrowText = styled('div')(({ theme: _theme }) => ({
  color: '#fff',
  fontSize: '0.95rem',
  fontWeight: 600,
  lineHeight: '1.00rem',
  textTransform: 'uppercase',
  left: '-100px',
  top: '12px',
  position: 'absolute',
  width: '70px',
  transform: 'rotate(315deg)',
  textAlign: 'center',
}));
const Container = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundSize: '100%',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 10% bottom 75%',
  position: 'relative',
  margin: '70px 0 50px 0',
  '@media screen and (max-width: 899px)': {
    display: 'none',
  },
  '@media screen and (min-width: 900px)': {
    minHeight: '420px',
  },
});
const MobileContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundSize: '450%',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 70% bottom -15%',
  position: 'relative',
  '@media screen and (max-width: 899px)': {
    minHeight: '700px',
  },
  '@media screen and (min-width: 900px)': {
    display: 'none',
  },
});
const TextContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'left',
  minHeight: 'inherit',
  '@media screen and (max-width: 899px)': {
    padding: '0 5%',
  },
  '@media screen and (min-width: 900px)': {
    padding: '0 10%',
  },
});
const VideoContainer = styled('div')({
  flexGrow: 0,
  flexShrink: 1,
  margin: '35px 0',
  '& .video': {
    aspectRatio: '16 / 9',
    width: '600px',
  },
});
const ActionButtonContainer = styled('div')({
  margin: '20px 0 27px 0',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  '@media screen and (max-width: 899px)': {
    alignItems: 'center',
  },
  '@media screen and (min-width: 900px)': {
    alignItems: 'flex-start',
  },
});
const ButtonContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
});
const EmbedContainer = styled('div')({
  '@media screen and (max-width: 899px)': {
    width: '269px',
  },
  '@media screen and (min-width: 900px)': {
    width: '700px',
  },
});

const FreeModule = ({ module }) => {
  const { fields } = module;

  const _router = useRouter();

  const [showVideoDialog, setShowVideoDialog] = useState(false);

  const hideVideoDialog = () => {
    setShowVideoDialog(false);
  };

  return (
    <React.Fragment>
      <MobileContainer
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.100),rgba(0, 0, 0, 0.1)), url(${fields.backgroundImage.url})`,
        }}
      >
        <KitGridContainer
          direction='row'
          style={{ border: '2px solid #fff', position: 'absolute', top: '5%', left: '8%', width: '90%', height: '90%' }}
        >
          <KitGridItem xs={12} sm={6} align='left' style={{ backgroundColor: 'transparent', height: '45%', width: '100%' }}></KitGridItem>
          <KitGridItem xs={12} sm={6} align='left' style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', height: '55%', width: '100%' }}>
            <TextContainer>
              <SeriesName style={{ color: fields.textColor }}>{fields.seriesName}</SeriesName>
              <VideoTitle style={{ color: fields.textColor }}>{fields.title}</VideoTitle>
              <Description style={{ color: fields.textColor }}>{fields.description}</Description>
              <ActionButtonContainer>
                <KitButton
                  color={fields.actionButtonColor}
                  onClick={(_e) => setShowVideoDialog(true)}
                  round
                  style={{ margin: '0', padding: '7px 40px', fontSize: '0.90rem', opacity: 1.0 }}
                >
                  {fields.actionButtonLabel}
                </KitButton>
              </ActionButtonContainer>
            </TextContainer>
          </KitGridItem>
        </KitGridContainer>
      </MobileContainer>
      <Container
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.100),rgba(0, 0, 0, 0.1)), url(${fields.backgroundImage.url})`,
        }}
      >
        <KitGridContainer
          direction='row'
          style={{ border: '2px solid #fff', position: 'absolute', top: '5%', left: '16.5%', width: '70%', height: '90%' }}
        >
          {/* <ArrowLeftCorner>
            <ArrowText>{fields.cornerText}</ArrowText>
          </ArrowLeftCorner> */}
          <KitGridItem xs={12} sm={6} align='left' style={{ backgroundColor: 'transparent', height: '100%', width: '100%' }}></KitGridItem>
          <KitGridItem xs={12} sm={6} align='left' style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', height: '100%', width: '100%' }}>
            <TextContainer>
              <SeriesName style={{ color: fields.textColor }}>{fields.seriesName}</SeriesName>
              <VideoTitle style={{ color: fields.textColor }}>{fields.title}</VideoTitle>
              <Description style={{ color: fields.textColor }}>{fields.description}</Description>
              <ActionButtonContainer>
                <KitButton
                  color={fields.actionButtonColor}
                  onClick={(_e) => setShowVideoDialog(true)}
                  round
                  style={{ margin: '0', padding: '7px 40px', fontSize: '0.90rem', opacity: 1.0 }}
                >
                  {fields.actionButtonLabel}
                </KitButton>
              </ActionButtonContainer>
            </TextContainer>
          </KitGridItem>
        </KitGridContainer>
      </Container>
      <Dialog open={showVideoDialog} onClose={hideVideoDialog} scroll='paper' maxWidth='md' fullWidth={true} aria-labelledby='Pop-up Video Viewer'>
        <React.Fragment>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', m: 'auto', width: 'fit-content' }}>
              <KitGridContainer direction='row'>
                <KitGridItem xs={12} align='left'>
                  <VideoContainer style={{ backgroundColor: getColorByAgilityName(fields.backgroundColor) }}>
                    <EmbedContainer className='prose max-w-full mx-auto' dangerouslySetInnerHTML={renderHTML(fields.embedVideoScript)} />
                  </VideoContainer>
                </KitGridItem>
              </KitGridContainer>
            </Box>
            <ButtonContainer>
              <KitButton
                round
                size='sm'
                color='primary'
                onClick={hideVideoDialog}
                style={{ margin: '0', padding: '7px 40px', fontSize: '0.90rem', opacity: 1.0 }}
              >
                Close
              </KitButton>
            </ButtonContainer>
          </DialogContent>
        </React.Fragment>
      </Dialog>
    </React.Fragment>
  );
};
export default FreeModule;

